/**
 * THE GENERATED CONCEPT — one line in, a whole build out. Pure: no React, no
 * DOM, no fetch. Everything here is a function of records the caller already
 * holds, so `verify-studio.mjs` drives the real module under Node.
 *
 * ─────────────────────────────────────────────────────────────────────────
 * ⚠️ WHAT THIS REPLACES, AND WHY THE OLD MODEL WAS WRONG
 *
 * Brian: *"it seems there truly isnt a difference in sourced build and
 * generated concepts... Sourced is supposed to be where we literally input all
 * the features we can with as much data as possible. A generated concept I
 * thought was supposed to be more of a preloaded heres what we want — a
 * championship ring for Duke Basketball 2027 ACC Champions — without any real
 * inputs or logos and it sources all the data we need."*
 *
 * He was right, and the measurement is unambiguous. The shipped "levels" were
 * the engine's `mode: render | generate` chip wearing new labels, and MEASURED
 * against the real backend assembler (`promptAssembler.js`, 2026-09-09) the two
 * modes produce prompts that are **5,319 vs 5,347 characters and differ in
 * exactly two places**: one 400-character MODE paragraph, and the word
 * `render`/`generate` echoed in the COMPONENTS header. Every other section —
 * beast, components, locked layer, output — is byte-identical. On the frontend
 * `validateStudio()` never reads `mode` at all: both levels required the same
 * 13 steps, the same picked components, the same hand-chosen crest.
 *
 * So the level was a word. A rep filled in everything either way.
 *
 * ⚠️ AND THE AXIS WAS ALREADY NAMED CORRECTLY, ONE FILE OVER. The Custom GPT's
 * own instructions (`ai-tools/ring-concept-gpt/INSTRUCTIONS.md` §5, §12) carry
 * TWO orthogonal axes and report them on separate lines:
 *
 *   OPERATING MODE   Exploratory Concept  ·  Directed Design   ← input burden
 *   BUILD MODE       render               ·  generate          ← how pixels are made
 *
 * We shipped the second axis under the first one's name. Brian's "sourced" is
 * the GPT's **Directed Design**; his "generated concept" is **Exploratory
 * Concept**. This module is that missing first axis.
 *
 * ─────────────────────────────────────────────────────────────────────────
 * THE RULE THIS MODULE OBEYS: IT SOURCES, IT NEVER INVENTS.
 *
 * Every value it puts in a build carries a RECEIPT naming the record it came
 * from, and the operator can correct any line before anything renders. A build
 * assembled from records with no visible provenance is how the wrong school
 * ends up on a ring — the receipt is not decoration, it is the safety.
 *
 * A field no record answers is OMITTED, exactly as in `buildPayload.js`. There
 * is no code path here that writes a guess.
 */

import { suggestAttributes, presentationLineSuggestion, venueContext } from './buildPayload.js'

const s = (v) => (v == null ? '' : String(v).trim())
const YEAR_RE = /\b(19|20)\d{2}\b/

/* Where a sourced value came from. These strings render verbatim on the
 * receipt, so they are the vocabulary the operator reads — not internal keys. */
export const SOURCE_CHAMPIONSHIP = 'championship record'
export const SOURCE_COMPANY = 'HubSpot company'
export const SOURCE_BRAND_KIT = 'brand kit — owned file'
export const SOURCE_DERIVED = 'derived'
export const SOURCE_DEFAULT = 'SR default'
export const SOURCE_BRIEF = 'your brief'
/* The operator typed it. Distinct from `you corrected it`, which replaces a
 * value a record DID answer — this one is the only source for a field no record
 * carries, and a receipt saying so is what stops it reading as sourced truth. */
export const SOURCE_KEYED = 'you keyed it'

/* ------------------------------------------------------------------ *
 * 1 · Reading the line
 * ------------------------------------------------------------------ */

/**
 * Read the operator's one line into something searchable. Deliberately thin:
 * it pulls out a year and normalizes whitespace, and otherwise hands the words
 * through untouched. Parsing "Duke Basketball" into a school and a sport by
 * rule is exactly the kind of cleverness that produces a confident wrong
 * answer — the RECORDS decide what those words mean, not a regex here.
 */
export function parseBrief(text) {
  const raw = s(text).replace(/\s+/g, ' ')
  const year = (YEAR_RE.exec(raw) || [])[0] || ''
  return { raw, year, query: raw, tokens: expandTokens(tokensOf(raw)), empty: raw.length === 0 }
}

/**
 * ⚠️ A GOVERNING BODY IS WRITTEN OUT ON THE RECORD AND ABBREVIATED IN SPEECH.
 * Measured live: the record for Brian's own example brief is
 * **"Atlantic Coast Conference Men's Basketball Championship"** — the object
 * spells it out and holds no row containing the token "acc" at all. A rep types
 * "ACC". Without this expansion the single most informative token in the brief
 * matches nothing, which is precisely how *"a championship ring for Duke
 * Basketball 2027 ACC Champions"* came back **"No championship record matches
 * that line"** while the right record sat in the universe.
 *
 * Expansion ADDS tokens and never removes one, so it can only ever improve a
 * match — and it is a fixed table, so it cannot invent a body nobody named.
 */
export const BODY_ALIASES = {
  acc: ['atlantic', 'coast', 'conference'],
  sec: ['southeastern', 'conference'],
  big10: ['big', 'ten'],
  b1g: ['big', 'ten'],
  pac12: ['pac', '12'],
  big12: ['big', '12'],
  aac: ['american', 'athletic', 'conference'],
  mac: ['mid', 'american', 'conference'],
  wcc: ['west', 'coast', 'conference'],
  mvc: ['missouri', 'valley', 'conference'],
  cusa: ['conference', 'usa'],
  ivy: ['ivy', 'league'],
  ncaa: ['ncaa', 'national'],
  naia: ['naia', 'national'],
  njcaa: ['njcaa', 'national'],
}

/** The brief's tokens plus any spelled-out form of a body it abbreviates. */
export function expandTokens(tokens) {
  const out = [...(tokens || [])]
  for (const t of tokens || []) {
    const add = BODY_ALIASES[t]
    if (add) for (const a of add) if (!out.includes(a)) out.push(a)
  }
  return out
}

/** Lowercased word tokens. Word boundaries only — see the NC/NCHSAA note below. */
export function tokensOf(text) {
  return s(text)
    .toLowerCase()
    .split(/[^a-z0-9]+/)
    .filter((t) => t.length > 1)
}

/* ------------------------------------------------------------------ *
 * 2 · Choosing the record
 * ------------------------------------------------------------------ */

/**
 * Score candidate championship records against the brief.
 *
 * ⚠️ TOKENS, NEVER SUBSTRINGS. `NC` is a substring of `NCHSAA`, and a naive
 * `includes()` here would score a North Carolina brief against every NCHSAA
 * record in the object. That exact bug shipped once in the hunt's prompt
 * composer and its own test agreed with it (CLAUDE.md, THE HUNT). Two-letter
 * state codes make it a certainty, not an edge case, so matching is on whole
 * tokens and the year is matched as a token too.
 */
export function rankChampionshipMatches(brief, records) {
  const want = new Set(brief && Array.isArray(brief.tokens) ? brief.tokens : [])
  if (!want.size) return []
  const list = Array.isArray(records) ? records : []

  return list
    .map((r) => {
      const hay = new Set(
        tokensOf([r.name, r.championship, r.title, r.school, r.champion, r.current_champion,
          r.sport, r.level, r.state, r.conference, r.year, r.season, r.division, r.segment]
          .map(s)
          .filter(Boolean)
          .join(' '))
      )
      let hits = 0
      for (const t of want) if (hay.has(t)) hits++
      // Normalized against the brief, so a long record name cannot out-score a
      // precise one just by containing more words.
      return { record: r, hits, score: hits / want.size }
    })
    .filter((c) => c.hits > 0)
    .sort((a, b) => b.score - a.score || s(a.record.name).length - s(b.record.name).length)
}

/**
 * Does this need the ONE disambiguating question?
 *
 * Brian's instruction is that an ambiguous brief asks one question — it does
 * NOT fall back to thirteen steps. So there are exactly three outcomes:
 * `none` (nothing matched), `choose` (ask once), `resolved` (proceed).
 *
 * The tie test is deliberately generous. Two records within 15% of each other
 * is not a confident answer, and silently taking the first one is how a build
 * gets the right school and the wrong division.
 */
export const MATCH_TIE_BAND = 0.15
export const MATCH_FLOOR = 0.34

export function resolveMatch(ranked) {
  const list = Array.isArray(ranked) ? ranked : []
  /* `none` means the universe answered and NOTHING matched — a real, rare
   * state. It is not what an imperfect brief should produce. */
  if (!list.length) return { state: 'none', choice: null, candidates: [] }
  const top = list[0]
  const second = list[1]
  const contested = !!second && top.score - second.score < MATCH_TIE_BAND
  if (top.score < MATCH_FLOOR || contested) {
    /* ⚠️ A WEAK MATCH IS A QUESTION, NOT A DEAD END. A brief naming the
     * customer ("Duke") scores low against a CONFERENCE record that names no
     * school, so the correct row is often a low scorer rather than an absent
     * one. Showing the ranked candidates costs the operator one click; refusing
     * to show them costs them the feature. */
    return { state: 'choose', choice: null, candidates: list.slice(0, 8) }
  }
  return { state: 'resolved', choice: top.record, candidates: list.slice(0, 8) }
}

/* ------------------------------------------------------------------ *
 * 3 · Composing the build
 * ------------------------------------------------------------------ */

/**
 * The receipt line. `path` is a dotted path into the build state so a human
 * correction can be applied generically — `applyReceipt()` below is the only
 * writer, and it is the only way a value reaches the build.
 */
function receipt(path, label, value, source, note = '') {
  return { id: path, path, label, value: s(value), source, note }
}

/** Read a dotted path off an object. */
export function readPath(obj, path) {
  return String(path).split('.').reduce((o, k) => (o == null ? o : o[k]), obj)
}

/** Write a dotted path immutably. The one writer into a concept build. */
export function applyReceipt(build, path, value) {
  const keys = String(path).split('.')
  const next = { ...build }
  let cur = next
  for (let i = 0; i < keys.length - 1; i++) {
    cur[keys[i]] = Array.isArray(cur[keys[i]]) ? [...cur[keys[i]]] : { ...cur[keys[i]] }
    cur = cur[keys[i]]
  }
  cur[keys[keys.length - 1]] = value
  return next
}

/**
 * Turn the resolved records into a COMPLETE build plus its receipts.
 *
 * `base` is `emptyBuild(config)` — the same starting state the 13-step flow
 * uses, so a concept and a sourced build are the same shape and go down the
 * same `buildPayload()` / `validateStudio()` path. The level changes what fills
 * the state in, never the contract it has to satisfy.
 *
 * ⚠️ THE ELEMENT SLOTS ARE LEFT EMPTY ON PURPOSE. Bezel, shanks and background
 * are what the operator picks in a sourced build; in a concept there is nothing
 * to pick from, which is the whole point of the level. Writing a plausible
 * default into them would be the same lie as autofilling a title.
 *
 * ⚠️ THE CREST IS NOT ONE OF THOSE. It arrives as the REAL OWNED FILE off the
 * school record, and that is the strongest property of this design: the
 * operator supplies no logo and still gets the customer's actual mark, because
 * the locked layer injects it rather than drawing it. No owned crest is a
 * BLOCKER, never something to draw around (`INSTRUCTIONS.md` §9).
 */
export function composeConcept({ base, brief = null, championship = null, school = null } = {}) {
  const build = { ...(base || {}) }
  const receipts = []
  const ch = championship || {}
  const sc = school || {}

  const put = (path, label, value, source, note) => {
    const v = s(value)
    if (!v) return
    Object.assign(build, applyReceipt(build, path, v))
    receipts.push(receipt(path, label, v, source, note))
  }

  const chName = s(ch.name || ch.championship || ch.title)
  const year = s(ch.year) || (YEAR_RE.exec(chName) || [])[0] || s(brief && brief.year)
  const schoolName = s(sc.name) || s(ch.school) || s(ch.champion || ch.current_champion)

  /* -- the keyed type: exact strings, every one off a record -------------- */
  put('keyed.titleTop', 'Title (top)', chName, chName ? SOURCE_CHAMPIONSHIP : SOURCE_BRIEF)
  put('keyed.titleBottom', 'Title (bottom)', schoolName, sc.name ? SOURCE_COMPANY : SOURCE_CHAMPIONSHIP)

  /* `suggestAttributes()` already reads both records and names its source per
   * chip — it has been doing so since the Builder shipped, and only ever fed a
   * step. This is the promotion Brian asked for: the same function, populating
   * a whole build. The chips a concept can use verbatim are the single-valued
   * ones; the source string is carried through unchanged, so a receipt says
   * exactly what a chip said. */
  const chips = suggestAttributes({ championship, school })
  const chip = (field, label) => chips.find((c) => c.field === field && c.label === label)

  const rec = chip('record', 'Season record')
  if (rec) put('keyed.record', 'Season record', rec.value, rec.source)

  const line = presentationLineSuggestion({ championship, school })
  if (line) put('keyed.presentationLine', 'Presentation line', line, SOURCE_DERIVED)

  const aud = chip('audience', 'Athletic director')
  if (aud) put('keyed.audience', 'Presented to', aud.value, aud.source)

  /* Names are a list. Only names a record actually states go in — the champion
   * and the head coach — and each one is its own receipt so a wrong coach can
   * be struck without losing the champion. */
  const names = []
  const pushName = (label, value, source) => {
    const v = s(value)
    if (!v || names.includes(v)) return
    names.push(v)
    receipts.push(receipt(`keyed.names.${names.length - 1}`, label, v, source))
  }
  pushName('Champion', ch.champion || ch.current_champion, SOURCE_CHAMPIONSHIP)
  pushName('Head coach', ch.coach || ch.headCoach || ch.champion_head_coach, 'coaches directory')
  if (names.length) build.keyed = { ...build.keyed, names }

  /* -- identity: the owned crest and the verified colours ----------------- */
  /* ⚠️ THE REF SHAPE IS THE PICKER'S, EXACTLY. `LogoCPicker` emits
   * `{ id, name, source: 'HubSpot-owned', url }` and `cleanRef()` in
   * buildPayload.js keeps only `name / id / source / description / url` — a
   * `{ ref: … }` object would be silently reduced to nothing and the crest
   * would vanish between the concept and the payload. The two levels have to
   * produce the SAME shape or they are not one contract. */
  const crest = s(sc.logoUrl || sc.logo || ch.logoUrl)
  if (crest) {
    build.logoC = {
      ...(sc.id ? { id: String(sc.id) } : {}),
      name: schoolName ? `${schoolName} crest` : 'Owned crest',
      source: 'HubSpot-owned',
      url: crest,
    }
    receipts.push(receipt('logoC.url', 'Client crest', crest, SOURCE_BRAND_KIT,
      'The real owned file, injected by the locked layer. No model draws this.'))
  }

  const colors = (Array.isArray(sc.colors) ? sc.colors : [])
    .map((c) => s(c && (c.hex || c)))
    .filter(Boolean)
  if (colors.length) {
    build.components = { ...build.components, teamColor: colors }
    receipts.push(receipt('components.teamColor', 'Team colours', colors.join(', '), SOURCE_BRAND_KIT))
  }

  const mascot = s(sc.mascot || ch.mascot)
  if (mascot) {
    build.components = { ...build.components, heroEmblem: mascot }
    receipts.push(receipt('components.heroEmblem', 'Hero emblem', mascot, SOURCE_COMPANY))
  }

  /* -- context for the venue preset: not keyed type, not rendered --------- */
  const v = venueContext({ championship, school })
  build.setting = { ...build.setting, sport: s(build.setting?.sport) || v.sport, region: s(build.setting?.region) || v.region }
  if (v.sport) receipts.push(receipt('setting.sport', 'Sport', v.sport, SOURCE_CHAMPIONSHIP))
  if (v.region) receipts.push(receipt('setting.region', 'Region', v.region, SOURCE_CHAMPIONSHIP))

  if (ch.id) build.championshipId = String(ch.id)

  /* ⚠️ `keyable` IS COMPUTED ONCE, AT COMPOSE TIME, ON PURPOSE.
   *
   * It lists the presentation fields no record answered, so the operator has
   * somewhere to key them. Deriving it live from the build instead would make
   * a field vanish from the list on its first keystroke — the input would
   * unmount mid-word. The list is a statement about what SOURCING found, which
   * does not change because a human typed something afterwards. */
  const keyable = unkeyedPresentation({ build, championship, school })

  return { build, receipts, keyable, year, schoolName, championshipName: chName }
}

/* ------------------------------------------------------------------ *
 * 3b · The presentation fields — keyed, never picked, never AI
 * ------------------------------------------------------------------ */

/**
 * The two keyed strings that belong to the presentation and that a championship
 * record routinely does NOT answer.
 *
 * ⚠️ WHY THIS EXISTS — a working field with no control.
 *
 * `keyed.presentationLine` and `keyed.audience` have been in the payload and in
 * `validateStudio()`'s warnings since the Builder shipped, and
 * `suggestAttributes()` has composed a presentation line for just as long. The
 * thirteen-step flow keys both at step 13. **Level 2 had no input for either.**
 * A concept only ever showed them when a record happened to answer — and
 * measured live on 2026-09-14 against the real object, `audience` is sourced
 * from `ch.owner || ch.audience`, which no championship record carries, so the
 * audience field was unreachable in level 2 in every case, not merely in the
 * sparse ones.
 *
 * So the rule is unchanged and the door is added: a field no record answers is
 * still OMITTED from the payload rather than blanked — it is simply now
 * keyable, which is what the personalization rule requires of it (typed, never
 * picked, never AI).
 */
export const KEYABLE_PRESENTATION = [
  {
    path: 'keyed.presentationLine',
    label: 'Presentation line',
    hint: 'The locked layer expects “School – Sport – Championship – Year”.',
  },
  {
    path: 'keyed.audience',
    label: 'Presented to',
    hint: 'Every presentation carries an audience. No record names it — it is yours to key.',
  },
]

/**
 * Which of those the composed build still has no value for, each carrying a
 * SUGGESTION and the suggestion's own source when one can be composed.
 *
 * ⚠️ IT WRITES NOTHING. There is no path from a suggestion into the build that
 * does not pass through an operator click — the same rule §7 pins for
 * `suggestAttributes()`, applied to the level that has no steps. An empty
 * suggestion is returned as '' rather than a half-built line, because a
 * plausible wrong presentation line is worse than a blank one.
 */
export function unkeyedPresentation({ build = null, championship = null, school = null } = {}) {
  const b = build || {}
  const chips = suggestAttributes({ championship, school })
  const out = []
  for (const f of KEYABLE_PRESENTATION) {
    if (s(readPath(b, f.path))) continue
    const chip = chips.find((c) => `keyed.${c.field}` === f.path)
    out.push({
      ...f,
      value: '',
      suggestion: chip ? s(chip.value) : '',
      suggestionSource: chip ? chip.source : '',
    })
  }
  return out
}

/* ------------------------------------------------------------------ *
 * 4 · What stops it
 * ------------------------------------------------------------------ */

/**
 * The concept-level blockers, in the operator's words.
 *
 * These are DELIBERATELY the same two locked-layer requirements
 * `validateStudio()` enforces — a title and an owned crest — stated where the
 * concept can act on them, because in this level the answer is "the record
 * does not carry one", not "you skipped step 1". The contract is unchanged;
 * only the remedy differs.
 */
export function conceptBlockers({ build = null, school = null, schoolLookup = null } = {}) {
  const b = build || {}
  const out = []
  if (['identity_mismatch', 'ambiguous_school'].includes(schoolLookup?.reason)) {
    out.push({
      code: 'school_identity',
      what: schoolLookup.reason === 'ambiguous_school'
        ? 'More than one school record matches. Choose the customer before rendering.'
        : 'The search results do not establish the customer’s school identity.',
      fix: 'Search for and explicitly choose the correct school below. Uploading a crest does not resolve the school identity.',
    })
  }
  if (!s(b.keyed && b.keyed.titleTop)) {
    out.push({
      code: 'no_title',
      what: 'No championship title could be sourced.',
      fix: 'Correct the title line on the receipt below, or pick a different championship record.',
    })
  }
  /* The same test `cleanRef()` applies — a ref with no `name` is dropped by the
   * payload, so a blocker that checked anything else would pass a build the
   * validator then refuses for a reason the concept never explained. */
  if (!(b.logoC && s(b.logoC.name))) {
    out.push({
      code: 'no_owned_crest',
      what: school && s(school.name)
        ? `${s(school.name)} has no owned crest on file.`
        : 'No owned crest on file for this school.',
      /* INSTRUCTIONS.md §9: "If the client has no owned mark on file, say so and
       * stop. Do not substitute one from the web." A concept that draws its own
       * crest is the exact failure the locked layer exists to prevent, and it is
       * worse here than in a sourced build because nobody chose it. */
      fix: 'Upload the crest here — it lands in HubSpot File Manager, so the build carries an OWNED file. A mark already staged is promoted through the Marks Library’s propose → approve queue.',
      /* THE BLOCKER CARRIES THE ACTION. See CREST_ROUTES. */
      routes: CREST_ROUTES,
    })
  }
  return out
}

/* ------------------------------------------------------------------ *
 * 4b · Getting the crest, from inside the blocked flow
 * ------------------------------------------------------------------ */

/**
 * THE ROUTES TO AN OWNED CREST, IN THE ORDER THAT ACTUALLY WORKS.
 *
 * ⚠️ THE MEASUREMENT THAT PUT UPLOAD FIRST, because the previous ordering was a
 * dead end for the population that needs it most.
 *
 * Brian: *"it is requiring a crest logo that is sourced from hubspot but not all
 * logos are in there especially in high school."*
 *
 * Measured live 2026-09-14, through the Studio's own door
 * (`GET /api/hubspot/schools` → `normalizeSchool()`):
 *
 *     high schools      0 of 335 sampled carry an owned crest      0.0%
 *     middle schools    0 of  23                                   0.0%
 *     colleges         20 of  33                                  60.6%
 *
 * and `GET /api/hubspot/logo-find` returned **nothing for 20 of 20** crest-less
 * high schools. There is no owned-logo PROPERTY on companies at all — 21
 * candidate `sr_*` names all answer "does not exist" on
 * `/api/coverage/property`. The crest is a real File Manager object under
 * `/hubfs/51263144/SR College Logos/…`, and the folder name says the rest: the
 * college logos were loaded, the high schools were not.
 *
 * `hs_logo_url` is NOT that crest and must never be counted as coverage — it is
 * HubSpot's auto-enriched domain logo on somebody else's server, so it is not an
 * owned asset (`owned-assets`). Proof it is not what the route serves: middle
 * schools are 62.2% on `hs_logo_url` and 0 of 20 showed a crest on screen.
 *
 * So for a high school the finder has nothing to find and the approve queue has
 * nothing staged to approve. **Upload is the only route that resolves the
 * blocker today**, and a blocker whose only named remedy is a dead end reads to
 * a rep as "this level does not work for my schools" — which is exactly the
 * report. The refusal itself is NOT weakened: the build still stops until a real
 * owned file is on it.
 */
export const CREST_ROUTES = [
  {
    id: 'upload',
    label: 'Upload the crest from this device',
    what: 'It goes to HubSpot File Manager, so the build carries an OWNED file rather than a link to someone else’s server.',
    measured: 'The only one of these three that resolves a high school today.',
    works: true,
  },
  {
    id: 'find',
    label: 'Search for an owned mark',
    what: 'Asks the owned-first finder for a mark already on file under this name.',
    measured: 'Measured 2026-09-14: found nothing for 20 of 20 crest-less high schools. Reliable for colleges.',
    works: true,
  },
  {
    id: 'marks',
    label: 'Marks Library — propose → approve',
    what: 'Promotes a mark already STAGED as -proposed to the live -primary file.',
    measured: 'Nothing to approve until something has been proposed, so it does not help a school with no mark at all.',
    works: false,
  },
]

/**
 * What the build's crest actually IS, as three states rather than two.
 *
 * ⚠️ `conceptBlockers` tests `logoC.name`, deliberately — it mirrors
 * `cleanRef()`, so the blocker and the payload agree about what survives. But a
 * `manual reference` ref also carries a name, so that test alone would let a
 * TYPED reference satisfy the owned-crest requirement. In the thirteen-step flow
 * a typed reference is a considered choice an operator made at step 1; in a
 * concept nobody chose it, so the crest control on this level refuses to write
 * one at all. This is the predicate it refuses by.
 *
 * Kept separate from `conceptBlockers` rather than folded into it: tightening
 * the blocker would change what the two levels refuse, and the levels share one
 * contract.
 */
export function crestState(build) {
  const ref = build && build.logoC
  if (!ref || !s(ref.name)) return 'none'
  return s(ref.url) ? 'owned' : 'reference'
}

/**
 * The one-line promise the level makes on screen, kept next to the code that
 * has to keep it. Rendered by `ConceptBrief.jsx`; asserted by the verify suite
 * so the words and the behaviour cannot drift apart.
 */
export const CONCEPT_PROMISE =
  'You supply no logo and no pickers — and the piece still carries the customer’s real owned crest, because the locked layer injects it rather than drawing it.'

/** Every source string a receipt may carry. The receipt vocabulary is closed. */
export const CONCEPT_SOURCES = [
  SOURCE_CHAMPIONSHIP,
  SOURCE_COMPANY,
  SOURCE_BRAND_KIT,
  SOURCE_DERIVED,
  SOURCE_DEFAULT,
  SOURCE_BRIEF,
  SOURCE_KEYED,
  'coaches directory',
]

